import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { paperUnderlineMap, splitUnderlinedDigits } from '../utils/paperDiff';
import { stripQuestionPaperText } from '../utils/questionPaper';
import { expandShieldedMatrices, shieldWordMatrices } from '../utils/questionFractions';
import FractionText from './FractionText';

function paint(children, map, matrices) {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      expandShieldedMatrices(String(child), matrices).forEach((part) => {
        if (part.type === 'matrix') {
          out.push(<FractionText key={out.length} text={part.text} />);
          return;
        }
        splitUnderlinedDigits(part.text, map).forEach((bit) => {
          const key = out.length;
          out.push(bit.under
            ? <span key={key} className="pd-ul">{bit.text}</span>
            : <React.Fragment key={key}>{bit.text}</React.Fragment>);
        });
      });
      return;
    }
    if (React.isValidElement(child) && child.props && child.props.children != null) {
      out.push(React.cloneElement(
        child,
        { ...child.props, key: child.key || `n-${out.length}` },
        paint(child.props.children, map, matrices)
      ));
      return;
    }
    out.push(child);
  });
  return out;
}

function marked(Tag, map, matrices) {
  return function MarkedBlock({ children, ...rest }) {
    return <Tag {...rest}>{paint(children, map, matrices)}</Tag>;
  };
}

/** Rubric markdown, with a line under the digit the answer names. */
export default function RubricMarkdown({ children }) {
  const plain = stripQuestionPaperText(children);
  const shielded = shieldWordMatrices(plain);
  const map = paperUnderlineMap(plain);
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: marked('p', map, shielded.matrices),
        li: marked('li', map, shielded.matrices),
        td: marked('td', map, shielded.matrices),
        th: marked('th', map, shielded.matrices),
        h1: marked('h1', map, shielded.matrices),
        h2: marked('h2', map, shielded.matrices),
        h3: marked('h3', map, shielded.matrices),
        h4: marked('h4', map, shielded.matrices)
      }}
    >
      {shielded.text}
    </ReactMarkdown>
  );
}
